import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck,
  Stethoscope,
  CalendarClock,
  Siren,
  Loader2,
  Plus,
  ThumbsUp,
  Ban,
  Video,
  Phone,
  MapPin,
  ExternalLink,
  AlertTriangle,
  Lock,
  Copy,
  Check,
  User,
  Users,
  Clock,
  Receipt,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button } from '../components';
import { PoortwachterDatatable } from '../components/PoortwachterDatatable';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { B2bProjectGrid } from '../components/B2bProjectGrid';
import {
  getTrajectenOverzicht,
  getPreventiefVerbruik,
  getB2bAfspraken,
  planB2bAfspraak,
  fiatteerB2bAfspraak,
  wijsB2bAfspraakAf,
  annuleerB2bAfspraak,
  B2B_SOORT_LABEL,
  getCrisisClaims,
  type B2bSoort,
  type B2bLocatie,
  type B2bAfspraak,
  type CrisisClaim,
} from '../services/lopendeTrajectenApi';

/**
 * [📊 Lopende Trajecten & Regie] — vier strikt gescheiden datatables:
 *  1. Preventief & anoniem verbruik (medewerkers-schil, alleen pseudoniemen)
 *  2. Wet Verbetering Poortwachter (verzuim-pipeline, Toegangs-Scope)
 *  3. Interne B2B-dienstverlening (service-agenda, Milestone-Grendel)
 *  4. Actieve calamiteiten & crisis-claims (Crisis-Pool + Garantiefonds)
 */

type Tab = 'preventief' | 'poortwachter' | 'b2b' | 'calamiteiten';

const TABS: Array<{ key: Tab; nr: number; label: string; description: string; icon: React.ComponentType<{ className?: string }>; kleur: string }> = [
  { key: 'preventief', nr: 1, label: 'Preventief & Anoniem', description: 'De medewerkers-schil: on-demand verbruik uit pijler 1, 2 en 3', icon: ShieldCheck, kleur: 'text-green-600' },
  { key: 'poortwachter', nr: 2, label: 'Wet Verbetering Poortwachter', description: 'De verzuim-pipeline met live Poortwachter-deadlines', icon: Stethoscope, kleur: 'text-red-600' },
  { key: 'b2b', nr: 3, label: 'Interne B2B-dienstverlening', description: 'Projectregie-grid met Budget-Grendel + service-agenda', icon: CalendarClock, kleur: 'text-blue-600' },
  { key: 'calamiteiten', nr: 4, label: 'Calamiteiten & Crisis-Claims', description: 'Alarmknop-incidenten, Crisis-Pool en Garantiefonds', icon: Siren, kleur: 'text-orange-600' },
];

const QK = {
  overzicht: ['lopende-trajecten', 'overzicht'],
  preventief: ['lopende-trajecten', 'preventief'],
  poortwachter: ['lopende-trajecten', 'poortwachter'],
  b2b: ['lopende-trajecten', 'b2b'],
  calamiteiten: ['lopende-trajecten', 'calamiteiten'],
};

const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });
const fmtEuro = (n: number | null | undefined) => euro.format(Number(n ?? 0));
const fmtDateTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const fmtUren = (n: number | null | undefined) => (n == null ? '—' : `${Number(n).toLocaleString('nl-NL', { maximumFractionDigits: 2 })} u`);
const apiMessage = (err: any, fallback: string): string => err?.response?.data?.message || fallback;
const toLocalInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';
const thClass = 'px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap';
const tdClass = 'px-3 py-2 text-sm text-gray-800 align-top';

const Badge: React.FC<{ tone: 'green' | 'red' | 'blue' | 'orange' | 'gray' | 'yellow'; children: React.ReactNode }> = ({ tone, children }) => {
  const tones = {
    green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-800',
    blue: 'bg-blue-100 text-blue-800',
    orange: 'bg-orange-100 text-orange-800',
    gray: 'bg-gray-100 text-gray-700',
    yellow: 'bg-yellow-100 text-yellow-800',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${tones[tone]}`}>{children}</span>;
};

const Leeg: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-center py-10 text-sm text-gray-500">{children}</div>
);

const Laden: React.FC = () => (
  <div className="flex items-center justify-center py-10 text-gray-500">
    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Laden…
  </div>
);

const Tabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="overflow-x-auto -mx-4 sm:mx-0">
    <table className="min-w-full divide-y divide-gray-200">{children}</table>
  </div>
);

const CopyButton: React.FC<{ value: string; label: string }> = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          toast.error('Kopiëren mislukt');
        }
      }}
      className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
      title={label}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {label}
    </button>
  );
};


// ═══ Tabel 1: Preventief & anoniem verbruik ══════════════════════════════════

const TRANSACTIE_LABEL: Record<string, { label: string; tone: 'green' | 'gray' | 'yellow' | 'blue' }> = {
  nog_niet_verbruikt: { label: 'Nog niet verbruikt', tone: 'gray' },
  open: { label: 'Open (maandfactuur)', tone: 'yellow' },
  gefactureerd: { label: 'Gefactureerd', tone: 'green' },
  betaald: { label: 'Stripe: betaald', tone: 'green' },
  wacht_op_stripe: { label: 'Stripe: wacht op betaling', tone: 'yellow' },
  geannuleerd: { label: 'Geannuleerd', tone: 'gray' },
};

const PreventiefTab: React.FC = () => {
  const { data, isLoading } = useQuery({ queryKey: QK.preventief, queryFn: getPreventiefVerbruik });
  if (isLoading) return <Laden />;
  const regels = data?.regels ?? [];
  const t = data?.totaal;
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-900">
        <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          <strong>Privacy-grendel:</strong> geen namen, geen afdelingen. U ziet uitsluitend geanonimiseerde pseudoniemen (SHA-256 telefoon-hash), de gekozen
          discipline, het verbruik en de Stripe-transactiestatus. Zo ziet u welk deel van het IKB/pop-up budget on-demand geconsumeerd wordt.
        </span>
      </div>
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ['Unieke medewerkers', String(t.unieke_pseudoniemen)],
            ['Lopende trajecten', String(t.trajecten)],
            ['Academy-boekingen', String(t.academy_boekingen)],
            ['Verbruikte uren', fmtUren(t.uren)],
            ['Academy on-demand', fmtEuro(t.academy_bedrag)],
          ].map(([l, v]) => (
            <div key={l} className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs text-gray-500">{l}</div>
              <div className="text-lg font-semibold text-gray-900">{v}</div>
            </div>
          ))}
        </div>
      )}
      {regels.length === 0 ? (
        <Leeg>Nog geen preventief verbruik. Zodra medewerkers via het anonieme portaal een professional of Academy-training boeken, verschijnt het hier.</Leeg>
      ) : (
        <Tabel>
          <thead className="bg-gray-50">
            <tr>
              <th className={thClass}>Pseudoniem</th>
              <th className={thClass}>Pijler</th>
              <th className={thClass}>Discipline / training</th>
              <th className={thClass}>Sessies</th>
              <th className={thClass}>Uren</th>
              <th className={thClass}>Bedrag</th>
              <th className={thClass}>Stripe / facturatie</th>
              <th className={thClass}>Laatste activiteit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {regels.map((r) => {
              const st = TRANSACTIE_LABEL[r.transactie_status] ?? { label: r.transactie_status, tone: 'gray' as const };
              return (
                <tr key={`${r.bron}-${r.referentie_id}`} className="hover:bg-gray-50">
                  <td className={`${tdClass} font-mono text-xs`}>{r.pseudoniem}</td>
                  <td className={tdClass}>{r.bron === 'sessie' ? <Badge tone="blue">Pijler 1 · Professional</Badge> : <Badge tone="green">Pijler 2 · Academy</Badge>}</td>
                  <td className={tdClass}>{r.discipline_label}</td>
                  <td className={tdClass}>{r.sessies}</td>
                  <td className={tdClass}>
                    {fmtUren(r.uren_verbruikt)}
                    {r.reistijd_uren > 0 && <span className="text-xs text-gray-500"> (+{fmtUren(r.reistijd_uren)} reistijd)</span>}
                  </td>
                  <td className={tdClass}>{r.bedrag == null ? '—' : fmtEuro(r.bedrag)}</td>
                  <td className={tdClass}>
                    <Badge tone={st.tone}>{st.label}</Badge>
                  </td>
                  <td className={tdClass}>{fmtDateTime(r.laatste_activiteit)}</td>
                </tr>
              );
            })}
          </tbody>
        </Tabel>
      )}
    </div>
  );
};

// ═══ Tabel 2: Wet Verbetering Poortwachter ═══════════════════════════════════
// 8-koloms grid + Ziekmeldings-Wizard + Digital Signing Wrapper: components/PoortwachterDatatable.tsx

// ═══ Tabel 3: Interne B2B-dienstverlening ════════════════════════════════════

const B2B_LOCATIE_LABEL: Record<B2bLocatie, string> = { online: 'Online (BeLagom-videolink)', telefonisch: 'Telefonisch', op_locatie: 'Op locatie' };
const B2B_STATUS: Record<B2bAfspraak['status'], { label: string; tone: 'blue' | 'orange' | 'green' | 'red' | 'gray' }> = {
  GEPLAND: { label: 'Gepland', tone: 'blue' },
  UITGEVOERD: { label: 'Uren ingediend — wacht op fiat', tone: 'orange' },
  GEFIATTEERD: { label: 'Gefiatteerd — vrijgegeven voor Stripe Connect', tone: 'green' },
  GEFACTUREERD: { label: 'Gefactureerd / uitbetaald', tone: 'green' },
  AFGEWEZEN: { label: 'Afgewezen', tone: 'red' },
  GEANNULEERD: { label: 'Geannuleerd', tone: 'gray' },
};

const NieuweB2bAfspraak: React.FC<{ pool: Array<{ professional_id: string; naam: string; discipline_label: string; uurtarief: number | null }>; onKlaar: () => void }> = ({ pool, onKlaar }) => {
  const start = new Date();
  start.setDate(start.getDate() + 1);
  start.setHours(10, 0, 0, 0);
  const eind = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const [vorm, setVorm] = useState({
    professional_id: pool[0]?.professional_id ?? '',
    soort: 'casemanagement_overleg' as B2bSoort,
    titel: '',
    omschrijving: '',
    start_tijd: toLocalInput(start),
    eind_tijd: toLocalInput(eind),
    locatie_type: 'online' as B2bLocatie,
  });
  const m = useMutation({
    mutationFn: () =>
      planB2bAfspraak({
        ...vorm,
        omschrijving: vorm.omschrijving || null,
        start_tijd: new Date(vorm.start_tijd).toISOString(),
        eind_tijd: new Date(vorm.eind_tijd).toISOString(),
      }),
    onSuccess: () => {
      toast.success('B2B-afspraak gepland');
      onKlaar();
    },
    onError: (e: any) => toast.error(apiMessage(e, 'Plannen mislukt')),
  });
  const prof = pool.find((p) => p.professional_id === vorm.professional_id);
  return (
    <div className="p-4 rounded-lg border border-blue-200 bg-blue-50 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className={labelClass}>Professional uit uw pool *</label>
          <select className={inputClass} value={vorm.professional_id} onChange={(e) => setVorm({ ...vorm, professional_id: e.target.value })}>
            {pool.map((p) => (
              <option key={p.professional_id} value={p.professional_id}>
                {p.naam} — {p.discipline_label}
              </option>
            ))}
          </select>
          {prof && <div className="text-xs text-gray-500 mt-1">Pool-tarief: {prof.uurtarief == null ? 'nog niet vastgelegd' : `${fmtEuro(prof.uurtarief)}/u`}</div>}
        </div>
        <div>
          <label className={labelClass}>Soort dienst *</label>
          <select className={inputClass} value={vorm.soort} onChange={(e) => setVorm({ ...vorm, soort: e.target.value as B2bSoort })}>
            {(Object.keys(B2B_SOORT_LABEL) as B2bSoort[]).map((s) => (
              <option key={s} value={s}>{B2B_SOORT_LABEL[s]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Vorm *</label>
          <select className={inputClass} value={vorm.locatie_type} onChange={(e) => setVorm({ ...vorm, locatie_type: e.target.value as B2bLocatie })}>
            {(Object.keys(B2B_LOCATIE_LABEL) as B2bLocatie[]).map((s) => (
              <option key={s} value={s}>{B2B_LOCATIE_LABEL[s]}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-3">
          <label className={labelClass}>Titel *</label>
          <input className={inputClass} value={vorm.titel} onChange={(e) => setVorm({ ...vorm, titel: e.target.value })} placeholder="bijv. Onboardingdag nieuwe teamleiders Q4" />
        </div>
        <div>
          <label className={labelClass}>Start *</label>
          <input type="datetime-local" className={inputClass} value={vorm.start_tijd} onChange={(e) => setVorm({ ...vorm, start_tijd: e.target.value })} />
        </div>
        <div>
          <label className={labelClass}>Einde *</label>
          <input type="datetime-local" className={inputClass} value={vorm.eind_tijd} onChange={(e) => setVorm({ ...vorm, eind_tijd: e.target.value })} />
        </div>
        <div>
          <label className={labelClass}>Toelichting</label>
          <input className={inputClass} value={vorm.omschrijving} onChange={(e) => setVorm({ ...vorm, omschrijving: e.target.value })} />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => m.mutate()} disabled={m.isPending || !vorm.professional_id || vorm.titel.trim().length < 3}>
          {m.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Inplannen'}
        </Button>
        <Button size="sm" variant="outline" onClick={onKlaar}>Annuleren</Button>
      </div>
    </div>
  );
};

const B2bRij: React.FC<{ a: B2bAfspraak }> = ({ a }) => {
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: QK.b2b });
    qc.invalidateQueries({ queryKey: QK.overzicht });
  };
  const fiatM = useMutation({
    mutationFn: () => fiatteerB2bAfspraak(a.id),
    onSuccess: () => {
      toast.success('Mijlpaal-sessie geaccordeerd — uitbetaling via Stripe Connect vrijgegeven');
      refresh();
    },
    onError: (e: any) => toast.error(apiMessage(e, 'Fiatteren mislukt')),
  });
  const afwijzenM = useMutation({
    mutationFn: (reden: string) => wijsB2bAfspraakAf(a.id, reden),
    onSuccess: () => {
      toast.success('Sessie afgewezen; de professional kan opnieuw indienen');
      refresh();
    },
    onError: (e: any) => toast.error(apiMessage(e, 'Afwijzen mislukt')),
  });
  const annuleerM = useMutation({
    mutationFn: () => annuleerB2bAfspraak(a.id),
    onSuccess: () => {
      toast.success('Afspraak geannuleerd');
      refresh();
    },
    onError: (e: any) => toast.error(apiMessage(e, 'Annuleren mislukt')),
  });
  const st = B2B_STATUS[a.status];
  const LocIcon = a.locatie_type === 'online' ? Video : a.locatie_type === 'telefonisch' ? Phone : MapPin;
  return (
    <tr className="hover:bg-gray-50">
      <td className={tdClass}>
        <div>{fmtDateTime(a.start_tijd)}</div>
        <div className="text-xs text-gray-500">tot {new Date(a.eind_tijd).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} · {fmtUren(a.uren_gepland)}</div>
      </td>
      <td className={tdClass}>
        <div className="font-medium">{a.titel}</div>
        <div className="text-xs text-gray-500">{B2B_SOORT_LABEL[a.soort]}</div>
        {a.omschrijving && <div className="text-xs text-gray-500 mt-0.5">{a.omschrijving}</div>}
      </td>
      <td className={tdClass}>
        <div>{a.professional_naam}</div>
        <div className="text-xs text-gray-500">{a.discipline_label}</div>
      </td>
      <td className={tdClass}>
        <div className="flex items-center gap-1 text-xs">
          <LocIcon className="w-3.5 h-3.5" /> {B2B_LOCATIE_LABEL[a.locatie_type]}
        </div>
        {a.video_link && (
          <div className="mt-1 flex items-center gap-2">
            <a href={a.video_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline">
              <ExternalLink className="w-3 h-3" /> Videolink
            </a>
            <CopyButton value={a.video_link} label="kopieer" />
          </div>
        )}
      </td>
      <td className={tdClass}>
        <div>{fmtUren(a.uren_geschreven)} {a.uurtarief != null && <span className="text-xs text-gray-500">× {fmtEuro(a.uurtarief)}</span>}</div>
        <div className="font-semibold">{a.bedrag == null ? '—' : fmtEuro(a.bedrag)}</div>
      </td>
      <td className={tdClass}>
        <Badge tone={st.tone}>{st.label}</Badge>
        {a.approved_at && (
          <div className="text-[11px] text-gray-500 mt-1">
            Fiat {fmtDateTime(a.approved_at)}{a.approved_ip ? ` · IP ${a.approved_ip}` : ''}
          </div>
        )}
        {a.payout_status && <div className="text-[11px] text-gray-500">Payout: {a.payout_status}{a.stripe_transfer_id ? ` · ${a.stripe_transfer_id}` : ''}</div>}
        {a.afwijzing_reden && <div className="text-[11px] text-red-700 mt-1">Reden: {a.afwijzing_reden}</div>}
      </td>
      <td className={tdClass}>
        {a.status === 'UITGEVOERD' && (
          <div className="flex flex-col gap-1">
            <Button size="sm" className="bg-green-600 hover:bg-green-700 border-green-600 whitespace-nowrap" onClick={() => fiatM.mutate()} disabled={fiatM.isPending}>
              {fiatM.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ThumbsUp className="w-3.5 h-3.5 mr-1" /> Accordeer Mijlpaal-Sessie</>}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={afwijzenM.isPending}
              onClick={() => {
                const reden = window.prompt('Reden van afwijzing (de professional ziet deze):');
                if (reden && reden.trim().length >= 3) afwijzenM.mutate(reden.trim());
              }}
            >
              <Ban className="w-3.5 h-3.5 mr-1" /> Afwijzen
            </Button>
          </div>
        )}
        {a.status === 'GEPLAND' && (
          <Button size="sm" variant="outline" disabled={annuleerM.isPending} onClick={() => window.confirm('Afspraak annuleren?') && annuleerM.mutate()}>
            Annuleren
          </Button>
        )}
      </td>
    </tr>
  );
};

const B2bAfsprakenTab: React.FC = () => {
  const [nieuw, setNieuw] = useState(false);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: QK.b2b, queryFn: getB2bAfspraken });
  if (isLoading) return <Laden />;
  const regels = data?.regels ?? [];
  const pool = data?.pool ?? [];
  const t = data?.totaal;
  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-start gap-3">
        <div className="flex-1 flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            <strong>Anti-fraude grendel:</strong> de professional dient na afloop de uren in; pas na uw klik op <em>Accordeer Mijlpaal-Sessie</em> wordt de
            uitbetaling via Stripe Connect vrijgegeven. Het fiat-bewijs (tijdstip + IP) blijft op de sessie staan.
          </span>
        </div>
        <Button onClick={() => setNieuw((v) => !v)} disabled={pool.length === 0} title={pool.length === 0 ? 'Geen actieve pool-professionals' : undefined}>
          <Plus className="w-4 h-4 mr-1" /> B2B-afspraak
        </Button>
      </div>
      {t && (
        <div className="grid grid-cols-3 gap-3">
          {[
            ['Wacht op fiat', String(t.te_fiatteren)],
            ['Gepland', String(t.gepland)],
            ['Gefiatteerd totaal', fmtEuro(t.gefiatteerd_bedrag)],
          ].map(([l, v]) => (
            <div key={l} className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs text-gray-500">{l}</div>
              <div className="text-lg font-semibold text-gray-900">{v}</div>
            </div>
          ))}
        </div>
      )}
      {nieuw && (
        <NieuweB2bAfspraak
          pool={pool}
          onKlaar={() => {
            setNieuw(false);
            qc.invalidateQueries({ queryKey: QK.b2b });
            qc.invalidateQueries({ queryKey: QK.overzicht });
          }}
        />
      )}
      {regels.length === 0 ? (
        <Leeg>Nog geen B2B-afspraken. Plan een onboardingdag, workshop, casemanagement-overleg of intervisie met een professional uit uw pool.</Leeg>
      ) : (
        <Tabel>
          <thead className="bg-gray-50">
            <tr>
              <th className={thClass}>Wanneer</th>
              <th className={thClass}>Dienst</th>
              <th className={thClass}>Professional</th>
              <th className={thClass}>Vorm</th>
              <th className={thClass}>Uren / bedrag</th>
              <th className={thClass}>Status</th>
              <th className={thClass}>Milestone-grendel</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {regels.map((a) => (
              <B2bRij key={a.id} a={a} />
            ))}
          </tbody>
        </Tabel>
      )}
    </div>
  );
};

/**
 * Tabel 3 heeft twee lagen: het langlopende projectregie-grid (Long-term B2B
 * Project Engine & Budget-Grendel, migratie 136) en de losse service-agenda
 * (b2b_appointments, migratie 129/132).
 */
const B2bTab: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const sub = params.get('b2b') === 'afspraken' ? 'afspraken' : 'projecten';
  const setSub = (v: 'projecten' | 'afspraken') => {
    const next = new URLSearchParams(params);
    next.set('tab', 'b2b');
    next.set('b2b', v);
    setParams(next);
  };
  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-sm">
        {(
          [
            ['projecten', 'Langlopende projecten & mijlpalen'],
            ['afspraken', 'Losse B2B-afspraken (service-agenda)'],
          ] as const
        ).map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => setSub(k)}
            className={`px-3 py-1.5 rounded-md transition ${sub === k ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-600 hover:text-gray-900'}`}
          >
            {l}
          </button>
        ))}
      </div>
      {sub === 'projecten' ? <B2bProjectGrid /> : <B2bAfsprakenTab />}
    </div>
  );
};

// ═══ Tabel 4: Calamiteiten & crisis-claims ═══════════════════════════════════
//
// 8 kolommen (developer-specificatie): 1 Incident-ID & locatie · 2 Inzet-omvang ·
// 3 Tijdas · 4 Responders · 5 Inzetduur (3-uurs cap) · 6 Regio & reiskosten ·
// 7 Garantiefonds-meter · 8 Factuur-plint. Berekening: src/services/crisisClaimBerekening.ts.

const CRISIS_STATUS: Record<CrisisClaim['status'], { label: string; tone: 'red' | 'orange' | 'green' | 'gray' }> = {
  OPEN: { label: 'ALARM · wacht op responder', tone: 'red' },
  ACCEPTED: { label: 'Uitgerukt', tone: 'orange' },
  CLOSED: { label: 'Afgerond', tone: 'green' },
  CANCELLED: { label: 'Geannuleerd', tone: 'gray' },
};

const CRISIS_FACTUUR_STATUS: Record<CrisisClaim['factuur_status'], string> = {
  GEEN: 'Geen factuur',
  PROGNOSE: 'Prognose · uren nog niet ingediend',
  WACHT_OP_VERZAMELFACTUUR: 'Wacht op SEPA-verzamelfactuur',
  OP_VERZAMELFACTUUR: 'Op verzamelfactuur',
};

const fmtKm = (km: number) => `${km.toLocaleString('nl-NL', { maximumFractionDigits: 1 })} km`;
const fmtUrenKort = (n: number) => n.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 2 });

/** "Dinsdag 15-09-2026 | 02:14 uur" */
const fmtMelddatum = (iso: string) => {
  const d = new Date(iso);
  const dag = d.toLocaleDateString('nl-NL', { weekday: 'long' });
  return {
    dag: dag.charAt(0).toUpperCase() + dag.slice(1),
    datum: d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    tijd: d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }),
  };
};

const DAGDEEL_TONE: Record<CrisisClaim['dagdeel']['shift'], 'blue' | 'orange' | 'gray'> = { dag: 'blue', avond: 'orange', nacht: 'gray' };

const CalamiteitenTab: React.FC = () => {
  const { data, isLoading } = useQuery({ queryKey: QK.calamiteiten, queryFn: getCrisisClaims, refetchInterval: 30_000 });
  if (isLoading) return <Laden />;
  const fonds = data?.fonds;
  const claims = data?.claims ?? [];
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 p-3 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-900">
        <Siren className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          <strong>Landelijke Crisis-Pool.</strong> Live-overzicht van alarmknop-incidenten. Binnen uw No-Claim tegoed is de uitruk voor 100% gedekt via het{' '}
          {fonds?.dekking_pct ?? 45}%-Garantiefonds (factuur € 0,00); daarbuiten geldt automatisch on-demand facturatie à{' '}
          {fmtEuro(fonds?.on_demand_uurtarief ?? 180)} per uur (minimaal 3 uur per professional) plus reiskosten bij buurregio-overloop, via de SEPA-verzamelfactuur.
        </span>
      </div>
      {fonds && !fonds.module_actief && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>De Crisis-Pool-module is nog niet geactiveerd op dit platform. Zodra een crisisabonnement per vestiging actief is, verschijnen incidenten hier live.</span>
        </div>
      )}
      {fonds && fonds.module_actief && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ['Crisisabonnementen', String(fonds.abonnementen)],
            ['Vrije uitrukken (No-Claim)', String(fonds.vrije_uitrukken)],
            ['Gebruikt dit jaar', String(fonds.gebruikte_uitrukken)],
            ['Resterend gedekt', String(fonds.resterend)],
            ['Garantiefonds-saldo', fmtEuro(fonds.fonds_saldo)],
          ].map(([l, v]) => (
            <div key={l} className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-xs text-gray-500">{l}</div>
              <div className="text-lg font-semibold text-gray-900">{v}</div>
            </div>
          ))}
        </div>
      )}
      {claims.length === 0 ? (
        <Leeg>Geen calamiteiten geregistreerd. Gelukkig.</Leeg>
      ) : (
        <Tabel>
          <thead className="bg-gray-50">
            <tr>
              <th className={thClass}>1 · Incident-ID & locatie</th>
              <th className={thClass}>2 · Inzet-omvang</th>
              <th className={thClass}>3 · Melddatum & tijdstip</th>
              <th className={thClass}>4 · Uitgerukte professionals</th>
              <th className={thClass}>5 · Geleverde uren</th>
              <th className={thClass}>6 · Regio & reiskosten</th>
              <th className={thClass}>7 · Garantiefonds-meter</th>
              <th className={thClass}>8 · Te betalen achteraf</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {claims.map((c) => {
              const st = CRISIS_STATUS[c.status];
              const dual = c.inzet_type === 'DUAL_RESPONSE';
              const melding = fmtMelddatum(c.incident_datum);
              const volledig = c.dekking.type === 'VOLLEDIG';
              const gedeeltelijk = c.dekking.type === 'GEDEELTELIJK';
              const geannuleerd = c.status === 'CANCELLED';
              return (
                <tr key={c.id} className={`hover:bg-gray-50 ${c.status === 'OPEN' ? 'bg-red-50/40' : ''}`}>
                  {/* 1 · Incident-ID & locatie */}
                  <td className={tdClass}>
                    <div className="flex items-center gap-1.5 font-semibold text-gray-900">
                      <Siren className={`w-4 h-4 flex-shrink-0 ${c.status === 'OPEN' ? 'text-red-600 animate-pulse' : 'text-orange-600'}`} />
                      <span>{c.incident_label}</span>
                    </div>
                    {c.vestiging_plaats && (
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" /> {c.vestiging_plaats}
                      </div>
                    )}
                    {c.omschrijving && <div className="text-xs text-gray-600 mt-1 max-w-[16rem] whitespace-pre-line">{c.omschrijving}</div>}
                    {c.details_vergrendeld && (
                      <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-1" title="Incidentdetails zijn uitsluitend inzichtelijk voor de hoofdgebruiker / HR-admin (Privacy-Grendel)">
                        <Lock className="w-3 h-3" /> Details vergrendeld (hoofd-HR)
                      </div>
                    )}
                    <div className="mt-1.5">
                      <Badge tone={st.tone}>{st.label}</Badge>
                    </div>
                    {c.video_url && c.status !== 'CLOSED' && !geannuleerd && (
                      <div className="mt-1">
                        <a href={c.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline">
                          <Video className="w-3 h-3" /> Videolink eerste opvang
                        </a>
                      </div>
                    )}
                  </td>
                  {/* 2 · Inzet-omvang */}
                  <td className={tdClass}>
                    <Badge tone={dual ? 'orange' : 'blue'}>
                      {dual ? <Users className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}
                      {dual ? 'Dual-Response' : 'Single-Response'}
                    </Badge>
                    <div className="text-xs text-gray-500 mt-1">{dual ? '2 experts simultaan' : '1 expert ingezet'}</div>
                    <div className="text-[11px] text-gray-500">
                      {c.punten_afgeboekt} van {c.aantal_gewenst} {c.aantal_gewenst === 1 ? 'dekkingspunt' : 'dekkingspunten'} afgeschreven
                    </div>
                  </td>
                  {/* 3 · Tijdas */}
                  <td className={tdClass}>
                    <div className="font-medium text-gray-900">{melding.dag} {melding.datum}</div>
                    <div className="text-sm text-gray-700">{melding.tijd} uur</div>
                    <div className="mt-1">
                      <Badge tone={c.dagdeel.weekend ? 'orange' : DAGDEEL_TONE[c.dagdeel.shift]}>
                        <Clock className="w-3 h-3 mr-1" />
                        {c.dagdeel.label}
                      </Badge>
                    </div>
                  </td>
                  {/* 4 · Responders */}
                  <td className={tdClass}>
                    {c.responders.length === 0 ? (
                      <span className="text-xs text-gray-500">{geannuleerd ? 'Geen uitruk' : `Nog niemand uitgerukt (${dual ? 'First-Two-Accept' : 'First-to-Accept'})`}</span>
                    ) : (
                      <ul className="space-y-1">
                        {c.responders.map((r) => (
                          <li key={r.id} className="text-xs">
                            <div>
                              <span className="font-medium text-gray-900">{r.naam}</span>
                              {dual && r.volgnummer && <span className="text-gray-400"> · #{r.volgnummer}</span>}
                            </div>
                            <div className="text-gray-500">{r.discipline_label}</div>
                          </li>
                        ))}
                        {c.responders.length < c.aantal_gewenst && c.status === 'OPEN' && (
                          <li className="text-[11px] text-red-700">Wacht op {c.aantal_gewenst - c.responders.length}e expert…</li>
                        )}
                      </ul>
                    )}
                  </td>
                  {/* 5 · Inzetduur */}
                  <td className={tdClass}>
                    {c.responders.length === 0 ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <>
                        <div className="font-semibold text-gray-900">{fmtUrenKort(c.uren_gefactureerd)} uur</div>
                        <div className="text-xs text-gray-500">
                          {c.uren_definitief ? `${fmtUrenKort(c.uren_gewerkt)} u geregistreerd` : 'uren nog niet ingediend'}
                        </div>
                        {c.cap_toegepast && (
                          <div className="text-[11px] text-orange-700 mt-0.5" title={`Wetstechnische minimale afname van ${c.min_uren_pp} uur per professional`}>
                            Minimale afname {c.responders.length} × {fmtUrenKort(c.min_uren_pp)} u = {fmtUrenKort(c.uren_minimaal)} u
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  {/* 6 · Regio & reiskosten */}
                  <td className={tdClass}>
                    <div className="flex items-center gap-1 text-gray-900">
                      <MapPin className="w-3.5 h-3.5 text-gray-500" /> {c.regio_naam}
                    </div>
                    {c.escalatie_label && (
                      <div className="text-[11px] text-orange-700 mt-0.5 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Cascade: {c.escalatie_label}
                      </div>
                    )}
                    {c.reis_overloop ? (
                      <div className="text-xs text-gray-700 mt-1">
                        <span className="font-medium">Buurregio-overloop:</span> {c.reis_km_totaal > 0 ? `${fmtKm(c.reis_km_totaal)} retour | ` : ''}
                        {fmtEuro(c.reiskosten)}
                        {!c.uren_definitief && c.reiskosten > 0 && <span className="text-gray-400"> (indicatie)</span>}
                      </div>
                    ) : (
                      c.responders.length > 0 && <div className="text-xs text-gray-500 mt-1">Eigen regio · geen reiskosten</div>
                    )}
                  </td>
                  {/* 7 · Garantiefonds-meter */}
                  <td className={tdClass}>
                    <div className={`flex items-start gap-1.5 text-xs font-semibold ${volledig ? 'text-green-700' : 'text-orange-700'}`}>
                      {volledig ? <ShieldCheck className="w-4 h-4 flex-shrink-0" /> : <ShieldAlert className="w-4 h-4 flex-shrink-0" />}
                      <span>{c.dekking_label}</span>
                    </div>
                    {gedeeltelijk && <div className="text-[11px] text-gray-500 mt-1">Fiscale Co-Payment: professional #{c.dekking.gedekt + 1} on-demand</div>}
                    {!volledig && c.tegoed_resterend != null && (
                      <div className="text-[11px] text-gray-500 mt-0.5">Tegoed vestiging nu: {c.tegoed_resterend}</div>
                    )}
                  </td>
                  {/* 8 · Factuur-plint */}
                  <td className={tdClass}>
                    <div className={`text-base font-semibold ${c.factuur_bedrag > 0 ? 'text-red-700' : 'text-green-700'}`}>{fmtEuro(c.factuur_bedrag)}</div>
                    {c.factuur_bedrag > 0 && (
                      <div className="text-[11px] text-gray-500">
                        {c.responders.filter((r) => r.gedekt_door === 'FACTUUR').map((r) => `${fmtUrenKort(r.uren_gefactureerd)} u × ${fmtEuro(c.uurtarief)}`).join(' + ')}
                        {c.reiskosten > 0 ? ` + ${fmtEuro(c.reiskosten)} reis` : ''}
                      </div>
                    )}
                    {c.factuur_status !== 'GEEN' && (
                      <div className="text-[11px] text-gray-600 mt-1 flex items-center gap-1">
                        <Receipt className="w-3 h-3" />
                        {c.factuur_status === 'OP_VERZAMELFACTUUR' && c.factuur_nummer ? `Verzamelfactuur ${c.factuur_nummer}` : CRISIS_FACTUUR_STATUS[c.factuur_status]}
                      </div>
                    )}
                    {volledig && c.responders.length > 0 && !geannuleerd && (
                      <div className="text-[11px] text-gray-500 mt-1">{fmtEuro(c.kosten_totaal)} gesaneerd uit Garantiefonds</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Tabel>
      )}
    </div>
  );
};

// ═══ Pagina ══════════════════════════════════════════════════════════════════

export const EmployerLopendeTrajectenPage: React.FC = () => {
  useCockpitTitle('Lopende Trajecten & Regie', 'Vier strikt gescheiden stromen: preventief, verzuim, B2B en calamiteiten');
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab') as Tab | null;
  const activeTab: Tab = TABS.some((t) => t.key === tabParam) ? (tabParam as Tab) : 'preventief';
  const setTab = (t: Tab) => setParams({ tab: t });

  const { data: overzicht } = useQuery({ queryKey: QK.overzicht, queryFn: getTrajectenOverzicht, refetchInterval: 60_000 });
  const tellers = useMemo<Record<Tab, number | null>>(
    () => ({
      preventief: overzicht?.preventief ?? null,
      poortwachter: overzicht?.poortwachter_open ?? null,
      b2b: overzicht?.b2b_te_fiatteren ?? null,
      calamiteiten: overzicht?.calamiteiten_actief ?? null,
    }),
    [overzicht]
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {TABS.map((t) => {
          const Icon = t.icon;
          const actief = t.key === activeTab;
          const teller = tellers[t.key];
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`text-left p-4 rounded-xl border transition ${actief ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className={`w-5 h-5 ${t.kleur}`} />
                  <span className="text-xs font-semibold text-gray-500">TABEL {t.nr}</span>
                </div>
                {teller != null && (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${teller > 0 && t.key !== 'preventief' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}>
                    {teller}
                  </span>
                )}
              </div>
              <div className="mt-2 font-semibold text-gray-900">{t.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>
            </button>
          );
        })}
      </div>

      <Card>
        {activeTab === 'preventief' && <PreventiefTab />}
        {activeTab === 'poortwachter' && <PoortwachterDatatable />}
        {activeTab === 'b2b' && <B2bTab />}
        {activeTab === 'calamiteiten' && <CalamiteitenTab />}
      </Card>

      <p className="text-xs text-gray-500">
        Organisatiegerichte projecten met mijlpaalbudget staan onder{' '}
        <Link to="/employer/projecten" className="underline">Lopende Organisatie Projecten</Link> en{' '}
        <Link to="/employer/mijlpalen" className="underline">Check &amp; Fiat mijlpalen</Link>.
      </p>
    </div>
  );
};

export default EmployerLopendeTrajectenPage;
